import { useId, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Calendar, X } from "lucide-react";

interface DateFieldProps {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  highlight?: boolean;
  /** Hint visuel pour la validation EN PRÉPARATION (cf. lib/bienPrepValidation.ts). */
  missingHint?: "required" | "mailing";
  /** Date max sélectionnable (ISO YYYY-MM-DD). Les jours après sont grisés et
   *  non cliquables. Ex : "Transfert élec fait le" ne peut pas être dans le futur. */
  maxDate?: string;
  /**
   * Rend le label SOUS le contrôle, en petit — même contrat que FormField et
   * SelectField. Nécessaire dans un `FieldGroup` : le libellé porteur est la
   * légende du groupe, chaque case ne garde qu'un micro-libellé.
   *
   * Une prop explicite et non une classe passée de l'extérieur : `cn` est un
   * simple clsx sans tailwind-merge, on ne peut pas écraser une classe du
   * composant depuis l'appelant.
   */
  labelBelow?: boolean;
  /** Valeur centrée dans le contrôle (dates courtes alignées en groupe). */
  align?: "left" | "center";
  /** Champ verrouillé : même boîte, même place, mais inerte. Cf. `FormField.readOnly`. */
  readOnly?: boolean;
}

const DAYS_FR = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

export function DateField({
  label,
  value = "",
  onChange,
  error,
  placeholder = "...",
  className,
  highlight: _highlight, // accepté pour compat API, ignoré (cf. note ci-dessous)
  missingHint,
  maxDate,
  labelBelow = false,
  align = "left",
  readOnly = false,
}: DateFieldProps) {
  void _highlight;
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const labelId = useId();
  // Popup en portail (document.body), positionné en `fixed` sur le rect du
  // déclencheur — un simple `position: absolute` se faisait couper par tout
  // ancêtre `overflow-y-auto` (ex. le panneau latéral étroit de Missions
  // prestataire, signalé Thibaud 2026-08-20 : "le calendrier et l'heure sont
  // coupés"). Même patron que PipelineActionsPopover.
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const today = new Date();
  const parsedDate = value ? new Date(value) : null;
  const [viewYear, setViewYear] = useState(parsedDate?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsedDate?.getMonth() ?? today.getMonth());
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const monthSubRef = useRef<HTMLDivElement>(null);
  const yearSubRef = useRef<HTMLDivElement>(null);
  const monthBtnRef = useRef<HTMLButtonElement>(null);
  const yearBtnRef = useRef<HTMLButtonElement>(null);

  const isFilled = value.length > 0;

  // Range années : 2015 → 2050 (couvre l'usage courant : historique + 25 ans).
  const YEARS_LIST = Array.from({ length: 2050 - 2015 + 1 }, (_, i) => 2015 + i);

  // Scroll la liste pour centrer l'année sélectionnée à l'ouverture — sinon
  // la liste s'ouvre toujours sur 2015 (haut du DOM), quelle que soit
  // l'année courante. Même patron que TimeField.tsx:144-158.
  useEffect(() => {
    if (!yearPickerOpen) return;
    requestAnimationFrame(() => {
      const selected = yearSubRef.current?.querySelector<HTMLElement>("[data-selected='true']");
      selected?.scrollIntoView({ block: "center", behavior: "auto" });
    });
  }, [yearPickerOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = !!ref.current && ref.current.contains(target);
      const insidePopup = !!popupRef.current && popupRef.current.contains(target);
      // Clic entièrement hors du déclencheur ET du popup portalé → ferme tout.
      if (!insideTrigger && !insidePopup) {
        setIsOpen(false);
        setMonthPickerOpen(false);
        setYearPickerOpen(false);
        return;
      }
      // Clic dans le calendrier mais hors du sub-dropdown mois (et hors du
      // bouton qui l'ouvre) → ferme le sub mois.
      if (
        monthPickerOpen &&
        monthSubRef.current &&
        !monthSubRef.current.contains(target) &&
        !monthBtnRef.current?.contains(target)
      ) {
        setMonthPickerOpen(false);
      }
      // Idem pour le sub année.
      if (
        yearPickerOpen &&
        yearSubRef.current &&
        !yearSubRef.current.contains(target) &&
        !yearBtnRef.current?.contains(target)
      ) {
        setYearPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [monthPickerOpen, yearPickerOpen]);

  // Refermer les sous-dropdowns dès qu'on ferme le calendrier.
  useEffect(() => {
    if (!isOpen) {
      setMonthPickerOpen(false);
      setYearPickerOpen(false);
    }
  }, [isOpen]);

  // Position du popup portalé : calquée sur le rect du déclencheur à
  // l'ouverture, recalculée si la page défile/redimensionne pendant que le
  // popup est ouvert (le scroll capturé en phase capture attrape aussi le
  // scroll d'un conteneur ancêtre, pas seulement `window`).
  useEffect(() => {
    if (!isOpen) { setPopupPos(null); return; }
    // Hauteur estimée du calendrier (header + jours de semaine + jusqu'à 6
    // lignes + padding) — sert uniquement à décider du sens d'ouverture, pas
    // à positionner : côté "au-dessus", on ancre par `bottom` (pas `top`) pour
    // que le popup colle exactement au-dessus du déclencheur quelle que soit
    // sa vraie hauteur rendue.
    const ESTIMATED_HEIGHT = 380;
    const update = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const left = r.left;
      const width = Math.max(r.width, 280);
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      // Signalé Thibaud 2026-09-12 : champ bas de page ("PEB FIN LE"), le
      // calendrier s'ouvrait toujours vers le bas et sortait de l'écran,
      // grille des jours invisible. On bascule au-dessus quand ça ne rentre
      // pas en dessous ET qu'il y a plus de place au-dessus.
      if (spaceBelow < ESTIMATED_HEIGHT && spaceAbove > spaceBelow) {
        setPopupPos({ left, width, bottom: window.innerHeight - r.top + 4 });
      } else {
        setPopupPos({ left, width, top: r.bottom + 4 });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  const showMissingBorder = !readOnly && !isFilled && !isOpen && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = readOnly
    ? "border border-solid border-transparent"
    : error
    ? "border border-solid border-danger"
    : showMissingBorder
      ? missingBorderClass
      : isOpen
        ? "border border-solid border-border-focus"
        : isFilled && isHovered
          ? "border border-dashed border-border-hover"
          : isFilled
            ? "border border-solid border-transparent"
            : isHovered
              ? "border border-dashed border-border-hover"
              : "border border-dashed border-border";

  // On ne colore le label QUE si le champ est vide ET tagué par le système de
  // validation EN PRÉPARATION/DISPONIBLE. Le prop highlight (c_level pré-existant)
  // n'affecte plus la couleur depuis qu'on a retiré sa légende.
  // Priorité : required > mailing > muted.
  const labelColorClass = isFilled
    ? "text-text-muted"
    : missingHint === "required"
      ? "text-danger"
      : missingHint === "mailing"
        ? "text-warning"
        : "text-text-muted";

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const selectDay = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    onChange?.(`${viewYear}-${m}-${d}`);
    setIsOpen(false);
  };

  const formatDisplay = (val: string) => {
    if (!val) return "";
    const parts = val.split("-");
    if (parts.length !== 3) return val;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const isSelected = (day: number) => {
    if (!parsedDate) return false;
    return (
      parsedDate.getFullYear() === viewYear &&
      parsedDate.getMonth() === viewMonth &&
      parsedDate.getDate() === day
    );
  };

  const isToday = (day: number) => {
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  // Comparaison string ISO (YYYY-MM-DD) : ordre lexicographique = ordre temporel.
  // Plus robuste que Date math (pas de drift timezone).
  const maxDateIso = maxDate || null;
  const isAfterMax = (day: number) => {
    if (!maxDateIso) return false;
    const m = String(viewMonth + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    const dayIso = `${viewYear}-${m}-${d}`;
    return dayIso > maxDateIso;
  };

  const labelNode = (
    <label
      id={labelId}
      className={cn(
        "font-medium tracking-wider uppercase truncate",
        labelBelow ? "text-[9.5px] text-center mt-1 px-0.5" : "text-xs px-3 mb-1",
        labelColorClass,
      )}
    >
      {label}
    </label>
  );

  return (
    <div className={cn("flex flex-col", className)} ref={ref}>
      {!labelBelow && labelNode}
      {/* Trigger : div+role="button" pour pouvoir nester un vrai bouton "clear"
          (HTML interdit un <button> dans un <button>). */}
      <div
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        aria-labelledby={labelId}
        aria-haspopup={readOnly ? undefined : "dialog"}
        aria-expanded={readOnly ? undefined : isOpen}
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm",
          align === "center" ? "text-center" : "text-left",
          "outline-none shadow-none transition-colors duration-150",
          "flex items-center justify-between gap-2",
          // `cn` est un simple clsx : on tranche le curseur ici, deux classes
          // `cursor-*` ne se départageraient pas.
          readOnly ? "cursor-default" : "cursor-pointer",
          borderClasses,
          isFilled ? "text-text-primary" : "text-text-muted"
        )}
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (!readOnly && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className={cn(align === "center" && "flex-1 text-center")}>
          {formatDisplay(value) || (readOnly ? "—" : placeholder)}
        </span>
        <div className="flex items-center gap-1">
          {isFilled && !readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
                setIsOpen(false);
              }}
              className="p-0.5 rounded hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Effacer la date"
              title="Effacer"
            >
              <X size={14} className="text-text-muted hover:text-danger" />
            </button>
          )}
          {/* L'icône calendrier promet un sélecteur : on la retire quand il n'y
              en a pas, en gardant sa place pour ne pas décaler la valeur. */}
          {readOnly ? (
            <span className="w-4 shrink-0" aria-hidden />
          ) : (
            <Calendar
              size={16}
              className={cn(
                "text-text-muted transition-colors duration-150",
                isOpen && "text-text-secondary"
              )}
            />
          )}
        </div>
      </div>
      {isOpen && !readOnly && popupPos && createPortal(
          <div
            ref={popupRef}
            style={{
              position: "fixed",
              left: popupPos.left,
              width: popupPos.width,
              zIndex: 9999,
              ...(popupPos.top !== undefined ? { top: popupPos.top } : { bottom: popupPos.bottom }),
            }}
            className="bg-surface border border-border rounded-md shadow-lg p-3"
          >
            {/* Header: month + year (cliquables pour dropdown rapide) + nav */}
            <div className="flex items-center justify-between mb-3 relative">
              <button
                type="button"
                className="p-1 hover:bg-gray-200 rounded cursor-pointer transition-colors"
                onClick={prevMonth}
              >
                <ChevronLeft size={16} className="text-text-secondary" />
              </button>
              <div className="flex items-center gap-1 text-sm font-medium text-text-primary">
                <button
                  ref={monthBtnRef}
                  type="button"
                  className="px-2 py-0.5 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                  onClick={() => { setMonthPickerOpen((o) => !o); setYearPickerOpen(false); }}
                >
                  {MONTHS_FR[viewMonth]}
                </button>
                <button
                  ref={yearBtnRef}
                  type="button"
                  className="px-2 py-0.5 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                  onClick={() => { setYearPickerOpen((o) => !o); setMonthPickerOpen(false); }}
                >
                  {viewYear}
                </button>
              </div>
              <button
                type="button"
                className="p-1 hover:bg-gray-200 rounded cursor-pointer transition-colors"
                onClick={nextMonth}
              >
                <ChevronRight size={16} className="text-text-secondary" />
              </button>

              {/* Dropdown mois — 3 colonnes pour tenir compact */}
              {monthPickerOpen && (
                <div ref={monthSubRef} className="absolute top-full left-0 right-0 mt-1 z-20 bg-surface border border-border rounded-md shadow-lg p-1 grid grid-cols-3 gap-0.5">
                  {MONTHS_FR.map((m, i) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setViewMonth(i); setMonthPickerOpen(false); }}
                      className={cn(
                        "px-2 py-1.5 rounded text-xs text-center transition-colors cursor-pointer",
                        i === viewMonth
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-text-primary hover:bg-gray-200"
                      )}
                    >
                      {m.slice(0, 4)}
                    </button>
                  ))}
                </div>
              )}

              {/* Dropdown année — scrollable, année courante centrée à l'ouverture
                  (signalé Thibaud 2026-09-15 : la liste s'ouvrait toujours sur
                  2015, obligeant à faire défiler ~11 ans jusqu'à l'année en
                  cours. Le commentaire promettait déjà ce comportement, jamais
                  implémenté). Même patron que TimeField.tsx:144-158. */}
              {yearPickerOpen && (
                <div ref={yearSubRef} className="absolute top-full left-0 right-0 mt-1 z-20 bg-surface border border-border rounded-md shadow-lg max-h-[200px] overflow-y-auto py-1">
                  {YEARS_LIST.map((y) => (
                    <button
                      key={y}
                      type="button"
                      data-selected={y === viewYear ? "true" : undefined}
                      onClick={() => { setViewYear(y); setYearPickerOpen(false); }}
                      className={cn(
                        "w-full px-3 py-1.5 text-sm text-center transition-colors cursor-pointer",
                        y === viewYear
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-text-primary hover:bg-gray-200"
                      )}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-0 mb-1">
              {DAYS_FR.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-text-muted py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-0">
              {/* Empty cells before first day */}
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-8" />
              ))}
              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const selected = isSelected(day);
                const todayMark = isToday(day);
                const disabled = isAfterMax(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={disabled}
                    className={cn(
                      "h-8 w-full rounded text-sm transition-colors",
                      disabled
                        ? "text-text-muted/40 cursor-not-allowed"
                        : "cursor-pointer",
                      !disabled && selected
                        ? "bg-accent text-accent-foreground font-medium"
                        : !disabled && todayMark
                          ? "bg-accent-light text-accent font-medium hover:bg-accent hover:text-accent-foreground"
                          : !disabled && "text-text-primary hover:bg-gray-200"
                    )}
                    onClick={() => !disabled && selectDay(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
      )}
      {labelBelow && labelNode}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
